import Vue from 'vue';
import i18n from '@/i18n';
import store from '@/store';

import Receipt from '@/components/print/Receipt';
import VueQrcode from '@chenfengyuan/vue-qrcode';

const HtmlGen = {

    /**
     * Format order as html
     *
     * @param {boolean} options.isReceipt is order a receipt
     * @param {boolean} options.isCopy is order a copy
     * @param {string} options.printingSector is order unpaid for a certain printing sector
     * @param {boolean} options.forceInvoiceForm force A4 form
     */
    orderToHtml(order, options = {}) {
        const elementId = 'print-container'

        const componentInstance = new Vue({
            ...Receipt,
            propsData: {
                id: elementId,
                order: order,
                options: options
            },
            i18n,
            store
        })

        let html = '';
        let el = null;

        try {
            componentInstance.$mount('#' + elementId);

            const printContainer = document.getElementById(elementId)
            el = printContainer.getElementsByClassName('printable-area')[0]
            html = el ? el.outerHTML : printContainer.outerHTML;
        } catch(error) {
            console.log(error.message)
        } finally {
            componentInstance.$destroy()
            if(el) {
                document.getElementById(elementId).innerHTML = html
            }
        }

        return html
    },

    qrCodeToHtml(qrCode) {
        const componentInstance = new Vue({
            ...VueQrcode,
            propsData: {
                value: qrCode,
                options: { width: 150 },
                tag: 'svg'
            },
        })

        const printContainer = document.getElementById('print-container')
        var div = document.createElement('div');
        div.id = 'print-container-content';
        printContainer.appendChild(div);

        let html = '';

        try {
            componentInstance.$mount('#print-container-content');
            html = componentInstance.$el.innerHTML
        } catch(error) {
            console.log(error.message)
        } finally {
            componentInstance.$destroy()
        }

        return html
    },

    /**
     * Convert encoded lines into html
     *
     * [L] - Align left, [C] - Align center, [R] - Align right (splits the line into equal columns).
     * [B] - Replace with one space. In case of text overflow break into 2 lines (both left aligned).
     * [S] - Fill out with spaces. In case of text overflow break into 2 lines (one left one right aligned).
     *
     * Examples:
     *
     * [L]left[C]center[R]right
     * [L]left[C]center[C]center[R]right
     * [L]text separated by[S]spaces
     * [L]text separated by[S]spaces overflow
     * [L]text with space[B]or break
     * [L]text with space[B]or break overflow
     * [C]overflowing text for which we create multiple lines and align all of them accordingly
     * [H11]text Highlight text - double height
     * [H12]text Highlight text - double height
     * [H22]text Highlight text - double width and height
     * [H00]text Highlight text - dense width and height
     * [HR] - line delimiter
     *
     * @param {string} text encoded line text
     * @returns {string} generated text
     */
    fromTextLine(text, delimiter = ' ') {
        const styles = {
            hr: 'border-bottom: 1px solid black; margin: 4.5px 0; height: 1px;',
            small: 'font-size: small;',
            line: 'color: black; min-height: 10px; line-height: 1.2; display: flex; justify-content: space-between; flex-wrap: wrap; align-items: center; break-inside: avoid;',
            table: 'justify-content: flex-start;',
        }

        if(text === '[HR]') {
            return `<div style="${styles['hr']}"></div>`;
        }

        const highlight = text.includes('[H12]') ? 'highlight12' : (text.includes('[H22]') ? 'highlight22' : (text.includes('[H00]') ? 'highlight00' : (text.includes('[H11]') ? 'highlight11' : null)))
        if(highlight) {
            text = text.replace('[H12]','').replace('[H22]','').replace('[H00]','').replace('[H11]','')
        }

        const divider = {}

        const customDelimiterMatch = text.match(/\[D.\]/)
        if(customDelimiterMatch) {
            delimiter = customDelimiterMatch[0][2]
            divider.enable = true
            divider.delimiter = ['-', '=', '_'].includes(delimiter) ? delimiter : '-'
            text = text.replace(/\[D.\]/g, '')
        }

        let colContents = text.replace(/\[L\]/g, '[COL]').replace(/\[C\]/g, '[COL]').replace(/\[R\]/g, '[COL]').split('[COL]').slice(1, 30)

        const cols = []
        colContents.forEach(content => {
            const parsedChars = cols.join('').length
            cols.push(text.slice(parsedChars, parsedChars + 3) + content)
        })

        let result = cols.length === 1 ? '' : `<div style="${styles['line']} ${styles['table']}">`
        cols.forEach(content => {
            result += this.genHtml(content, cols.length === 1, divider, highlight).join('')
        })

        return cols.length === 1 ? result : (result + '</div>')
    },

    genHtml(line, singleColumn = true, divider = {}, highlight = null) {
        const styles = {
            divider: `color: black; display: flex; justify-content: center; align-items: center; line-height: 1.2; ${highlight === 'highlight12' ? 'min-height: 28px' : 'min-height: 18px'};`,
            dividerLines: 'width: 100%;',
            dividerLine: 'width: 100%; border-bottom: 1.5px dashed; margin: 1.5px 0;',
            dividerText: 'display: flex; justify-content: center; align-items: center; background: white; padding: 0px 13px; flex: none;',
            highlight12: 'transform: scale(1, 1.7); line-height: 1.2;',
            highlight22: 'font-size: 150%; font-weight: 500;',
            highlight00: 'font-size: 70%;',
            highlight11: 'font-weight: 600;',
            flex1: 'flex: 1;',
            borderBottom: 'border-bottom: 1.5px solid black; align-self: flex-end;',
            line: 'color: black; min-height: 10px; line-height: 1.2; display: flex; justify-content: space-between; flex-wrap: wrap; align-items: center; white-space: break-spaces; break-inside: avoid;',
            left: 'justify-content: flex-start; text-align: left;',
            center: 'justify-content: center; text-align: center;',
            right: 'justify-content: flex-end; text-align: right;',
            justifySpaceBetween: 'justify-content: space-between;',
        }

        const alignment = line.slice(0, 3)
        const alignStyle = alignment === '[R]' ? styles['right'] : (alignment === '[C]' ? styles['center'] : styles['left'])

        const textContent = line.slice(3)
        const fillSpaces = textContent.includes('[S]')
        const spaceOrBreak = textContent.includes('[B]')

        const highlightStyle = highlight ? styles[highlight] : ''

        if(divider.enable) {
            if(divider.delimiter === '_') {
                return [`<div style="${styles['line']} ${highlightStyle}"><span>${textContent}</span><span style="${styles['flex1']} ${styles['borderBottom']}"></span></div>`]
            }

            return [
                `<div style="${styles['divider']}">` +
                    `<div style="${styles['dividerLines']}">` +
                        (divider.delimiter === '-' ? `<div style="${styles['dividerLine']}"></div>` : `<div style="${styles['dividerLine']}"></div><div style="${styles['dividerLine']}"></div>`) +
                    `</div>` +
                    (textContent ? `<div style="${styles['dividerText']} ${highlightStyle}">${textContent}</div>` : '') +
                    `<div style="${styles['dividerLines']}">` +
                        (divider.delimiter === '-' ? `<div style="${styles['dividerLine']}"></div>` : `<div style="${styles['dividerLine']}"></div><div style="${styles['dividerLine']}"></div>`) +
                    `</div>` +
                `</div>`
            ]
        }

        if(spaceOrBreak) {
            return [`<div style="${styles['line']} ${highlightStyle}">${textContent.replace('[B]', ' ')}</div>`]
        }

        if(fillSpaces) {
            const contents = textContent.split('[S]')
            return [`<div style="${styles['line']} ${styles['justifySpaceBetween']} ${highlightStyle}"><span>${contents[0]}</span><span>${contents[1]}</span></div>`]
        }

        if(!singleColumn) {
            return [`<span style="${alignStyle} ${styles['flex1']} ${highlightStyle}">${textContent}</span>`]
        }

        return [`<div style="${styles['line']} ${alignStyle} ${highlightStyle}">${textContent}</div>`]
    }
};

export default HtmlGen;

export { HtmlGen };
